/**
 * The trial-end sheet — the ONE auto-presented paywall moment on native.
 *
 * Model (2026-09-05): every new account gets a card-free 14-day GOAT trial
 * (server-granted, all platforms). When it ends the app keeps working on the
 * Free plan. The first time the app sees that account on Free with an
 * expired trial, it presents the paywall once as `trial_ended` — closable
 * from the first frame — and never auto-presents again. Every other paywall
 * opening is the user's own tap on a free wall.
 *
 * Accounts that never had a server trial (legacy iOS signups from before the
 * trial covered iOS) get no sheet at all: the upsell for them lives inside
 * the locked features.
 *
 * "Seen" is per account and per device, mirrored into the durable native
 * Preferences store (window.electron.store) because iOS WKWebView evicts
 * localStorage under memory pressure — same pattern as the onboarding flag.
 * Keyed by user id, so it deliberately survives logout (see the
 * keyed-not-wiped convention in CLAUDE.md).
 */

const KEY_PREFIX = 'mixgoat:trialEndSeen:';

export const trialEndSeenKey = (userId) => `${KEY_PREFIX}${userId}`;

function durableStore() {
    return (typeof window !== 'undefined' && window.electron && window.electron.store) || null;
}

export function readTrialEndSeen(userId) {
    if (!userId) return false;
    try { if (localStorage.getItem(trialEndSeenKey(userId))) return true; } catch { /* storage unavailable */ }
    try {
        const store = durableStore();
        if (store && store.get(trialEndSeenKey(userId))) return true;
    } catch { /* store unavailable */ }
    return false;
}

export function writeTrialEndSeen(userId) {
    if (!userId) return;
    const value = new Date().toISOString();
    try { localStorage.setItem(trialEndSeenKey(userId), value); } catch { /* storage unavailable */ }
    try {
        const store = durableStore();
        if (store) store.set(trialEndSeenKey(userId), value);
    } catch { /* store unavailable */ }
}

/**
 * Pure decision: should the trial-end sheet present right now?
 * Every input is a plain value so App.js stays a thin wiring layer and this
 * can be tested without React.
 */
export function shouldPresentTrialEndSheet({
    freeForAll,
    isNative,
    isAuthenticated,
    tierLoaded,
    userId,
    effectiveTier,
    isTrialExpired,
    onboardingDecided,
    onboardingActive,
    seen,
}) {
    if (freeForAll) return false;            // open model: nothing auto-presents
    if (!isNative) return false;             // desktop/web have their own flows
    if (!isAuthenticated || !tierLoaded || !userId) return false;
    if (effectiveTier !== 'free') return false;   // trial / IAP / Stripe / comp
    if (!isTrialExpired) return false;       // no server trial ended → no sheet
    // Hold while onboarding is still deciding or showing, otherwise the sheet
    // presents behind the onboarding overlay and burns its once-only flag.
    if (!onboardingDecided || onboardingActive) return false;
    if (seen) return false;
    return true;
}
